import {
  CompStructure,
  MenuData,
  MenuItem,
  PagesData,
  PagesStructure,
  Pointer,
  ColorPalette,
  CompRef,
} from '@wix/document-services-types'

import type {NotificationBannerProps} from '@wix/wix-base-ui'

export type {SingleLayoutData} from '@wix/document-services-types'

import {Monitoring, PageDefinition, PremiumOptions} from './definitions'

export type {
  PageData,
  PageData as PageProperties,
  PageDefinition,
} from './definitions/pageDefinition'

export type {ResponsiveLayout} from './definitions/responsiveLayout'

export interface PageRef extends CompRef {}

export interface ComponentRef extends CompRef {}

export interface CollapsedExpandedComponentRef {
  componentRef: ComponentRef
  role: string
}

export interface Connection {
  role: string
  controllerRef: ComponentRef
  isPrimary?: boolean
  config?: any
  subRole?: string
  type?: string
}

export enum LinkTypes {
  NoLink = 'NoLink',
  PageLink = 'PageLink',
  AnchorLink = 'AnchorLink',
  ExternalLink = 'ExternalLink',
  DocumentLink = 'DocumentLink',
  PhoneLink = 'PhoneLink',
  EmailLink = 'EmailLink',
  LoginToWixLink = 'LoginToWixLink',
  DynamicPageLink = 'DynamicPageLink',
  EdgeAnchorLink = 'EdgeAnchorLinks',
  PopupLink = 'PopupLink',
  FormSubmitButtonLink = 'FormSubmitButtonLink',
}

export type LinkTypeStrings =
  | 'NoLink'
  | 'PageLink'
  | 'AnchorLink'
  | 'ExternalLink'
  | 'DocumentLink'
  | 'PhoneLink'
  | 'EmailLink'
  | 'LoginToWixLink'
  | 'DynamicPageLink'
  | 'EdgeAnchorLinks'
  | 'PopupLink'
  | 'FormSubmitButtonLink'

export interface LinkData {
  type: LinkTypeStrings
}

export interface PageLink extends LinkData {
  pageId: string
}

export interface DynamicPageLink extends LinkData {
  routerId: string
  innerRoute: string
  anchorDataId?: string
}

export interface RouterRef {
  id: string
}

export interface DynamicRoute {
  prefix: string
  innerRoute: string
  routerId: string
}

export type Link = object

export enum DeviceType {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

export interface RouterPage {
  pageRef: PageRef
  pageRoles: string[]
}

export interface commonConfig {
  brand: string
  host?: string
}

export interface RouterData {
  id: string
  prefix: string
  config: Record<string, string>
  pages: RouterPage[]
  roleVariations: Record<string, string[]>
}

export type Font = object

export interface FontOption {
  lang: string
  fonts: Font[]
}

export enum TPAComponentType {
  Page = 'PAGE',
  Widget = 'WIDGET',
}

export type TPAComponentTypeStrings = 'PAGE' | 'WIDGET'

export interface Mode {
  label: string
  modeId: string
  params: string
  type: string
}

export interface BehaviorData {
  action: string
  delay: number
  duration: number
  name: string
  params?: {
    direction?:
      | 'top'
      | 'top left'
      | 'top right'
      | 'left'
      | 'bottom'
      | 'bottom left'
      | 'bottom right'
      | 'right'
      | 'center'
      | 'cw'
      | 'ccw'
    power?: 'soft' | 'medium' | 'hard'
    bounce?: 'soft' | 'medium' | 'hard'
    angle?: number
    distance?: number
    cycles?: number
  }
  targetId: string
  viewMode: 'DESKTOP' | 'MOBILE'
}

interface OpenPopupBehavior {
  name: 'openPopup'
  params: {
    delay: number
    openInDesktop: boolean
    openInMobile: boolean
  }
}

export type PopupPageBehavior = OpenPopupBehavior

export interface AppFields {
  platform?: {
    baseUrls?: Record<string, string>
    editorScriptUrl?: string
    studio?: {
      siteHeaderUrl: string
    }
  }
}

export interface Component {
  componentId: string
  name?: string
  type: string
  data: any
}

/**
 * Data about an application that is always passed to the application in the editorReady method.
 */
export interface InitialAppData {
  /**
   * URL of the application's editor script that is running.
   */
  editorScriptUrl: string
  editorSessionId?: string
  /**
   * Application instance.
   */
  instance: string
  /**
   * Application instanceId.
   */
  instanceId: string
  metaSiteId?: string
  userId: string
  viewMode: 'DESKTOP' | 'MOBILE'
  monitoring?: Monitoring
  /**
   * editor language code.
   */
  languageCode?: string
  /**
   * @deprecated Use appDefinitionId instead
   * The application's applicationId, which is a hash value assigned to each application installed on a site.
   */
  applicationId: number
  /**
   * The application's appDefinitionId, which is the GUID generated in the Dev Center when you create your application.
   */
  appDefinitionId: string
  appFields: AppFields
  components: Component[]
}

export interface SessionStateMap {
  [index: string]: string
}

export enum WidgetInstallationType {
  Open = 'open',
  Closed = 'closed',
}

export type WidgetInstallationTypeStrings = 'open' | 'closed'

export interface ErrorInfo {
  error: any
  methodName: string
}

export type gfppOriginStrings = 'gfpp' | 'double_click'

export type LanguageCode = string

export type SiteTextPresetValue = {
  editorKey: string
  fontFamily: string
  lineHeight: string
  size: string
  style: string
  value: string
  weight: string
}
export type SiteTextPresetName =
  | 'Body-L'
  | 'Body-M'
  | 'Body-S'
  | 'Body-XS'
  | 'Heading-L'
  | 'Heading-M'
  | 'Heading-S'
  | 'Heading-XL'
  | 'Menu'
  | 'Page-title'
  | 'Title'
export type SiteTextPresets = {
  [key in SiteTextPresetName]: SiteTextPresetValue
}

export type TPAPublicDataScope = 'APP' | 'COMPONENT'

type JsonObjectValueType = string | number | boolean | Date | JsonObject
type JsonObjectValue = JsonObjectValueType | JsonObjectValueType[]
export type JsonObject = {
  [x: string]: JsonObjectValue
}
export type TPAPublicDataValue = string | number | boolean | JsonObject

export type TPAPublicDataEntry = Record<string, TPAPublicDataValue>
export type TPAPublicData<
  AppScopeValues extends TPAPublicDataEntry | undefined =
    | TPAPublicDataEntry
    | undefined,
  ComponentScopeValues extends TPAPublicDataEntry | undefined =
    | TPAPublicDataEntry
    | undefined,
> = {
  APP: Partial<AppScopeValues>
  COMPONENT: Partial<ComponentScopeValues>
}

export type SDKExtensionContext = {context: 'extension'}
export type SDKDefaultContext = {context: 'default'}
export type SDKContext = SDKDefaultContext | SDKExtensionContext

export type ContextAwareOptions<
  Context extends SDKContext,
  Options extends Record<string, any> | void,
  ExtensionAdditionalOptions extends Partial<{
    appDefinitionId: string
    applicationId: number
  }>,
> = Context extends SDKExtensionContext
  ? Options extends void
    ? ExtensionAdditionalOptions
    : Options & ExtensionAdditionalOptions
  : Options

export type AppData = {
  appDefinitionId?: string
  applicationId?: number
  context: 'default' | 'extension'
}

export interface ComponentClientSpecMapEntry {
  id: string
  widgetId: string
  applicationId: string
  appDefinitionId: string
  type: string
  metaData: {
    isPreset: boolean
    schemaVersion: string
    isHidden: boolean
    pageId: string
  }
  referenceId: string
  pageId: string
}

export type SiteColor = {
  name: string
  value: string
  reference: string
}

export type AppInstancePayload = {
  instanceId: string
  appDefId: string
  metaSiteId: string
  signDate: string
  uid: string
  permissions: string
  demoMode: boolean
  originInstanceId: string
  biToken: string
  siteOwnerId: string
  siteMemberId: string
  expirationDate: string
  loginAccountId: string
}

export type DeveloperModeStatus = 'WRITE' | 'READ_ONLY' | 'OFF'

type ElementLink<IsPremium extends boolean = false> = IsPremium extends true
  ? {
      text: string
    }
  : {
      text: string
      helpId: string
    }

type TooltipData<IsPremium extends boolean = false> = {
  content?: string
  link?: ElementLink<IsPremium>
}

export type ElementIdentifier = {
  role: string // to be used by platform in editor
  subRole?: string // to be used by platform in editor
}

export type ElementData = {
  label?: string // if not provided display name will be taken.
  identifier: ElementIdentifier
  index: number
  categoryId?: ElementCategoryData['id']
  sectionId?: ElementSectionData['id']
  tooltipData?: TooltipData<false>
  premium?: PremiumOptions & {
    isPremium: boolean
    tooltipData?: TooltipData<true>
  }
}

export type HierarchicalItem = {
  id: string
  title?: string
  tooltipData?: TooltipData
}

export type ElementCategoryData = HierarchicalItem & {
  enableToggleAll?: boolean
}

export type ElementSectionData = HierarchicalItem

export enum AddElementsPanelBannerIcons {
  AllBreakpoints = 'all-breakpoints',
}

export type NotificationBannerSerializable = Omit<
  NotificationBannerProps,
  'children' | 'link' | 'prefix' // omitting props that include react components (not seralizable).
> & {
  prefixIcon?: AddElementsPanelBannerIcons
  link?: {
    label: string
    helpId: string
  }
}

interface AddElementsPanelBaseOptions {
  helpId?: string
  widgetRef: ComponentRef
  elementsData: ElementData[]
  categoriesData: ElementCategoryData[]
  sectionsData?: ElementSectionData[]
  withDivider: boolean
  subtitle?: string
}

export interface AddElementsPanelOptions extends AddElementsPanelBaseOptions {
  compToSelect?: ComponentRef
  topBanner?: NotificationBannerSerializable
}

export type AddComponentHandler = (
  elementIdentifier: ElementIdentifier,
  componentRef?: ComponentRef,
) => Promise<Array<ElementIdentifier>> | Promise<void> | void
export type RemoveComponentHandler = (
  componentRef: ComponentRef,
  elementIdentifier: ElementIdentifier,
) => Promise<Array<ElementIdentifier>> | Promise<void> | void

export interface OpenElementsPanelOptions extends AddElementsPanelBaseOptions {
  addComponentHandler: AddComponentHandler
  removeComponentHandler: RemoveComponentHandler
}

export type ConfirmationPanelFootnote = {
  footnoteText: string
  urlText?: string
  urlLink?: string
}

export type IExtraNamespaces = 'appBuilder' | 'responsive'

export interface IGetDataByAppDef {
  applicationId: number
  appDefinitionId: string
  instance: string
  instanceId: string
  isWixTPA: boolean
  appFields: AppFields
}

const AppController = 'platform.components.AppController'
type AppControllerType = typeof AppController

const AppWidget = 'platform.components.AppWidget'
type AppWidgetType = typeof AppWidget

const Page = 'mobile.core.components.Page'
type PageType = typeof Page

type ComponentType = AppControllerType | AppWidgetType | PageType | string

type AppControllerOptions = {
  appDefinitionId: string
  controllerType: string
  name?: string
  settings?: Record<string, unknown>
  layout?: ComponentDefinition['layout']
}
type AppWidgetOptions = {
  appDefinitionId: string
  controllerType: string
  name?: string
  layout?: ComponentDefinition['layout']
  children: [ComponentDefinition]
}
type PageOptions = {
  data: PageDefinition['data']
  components?: ComponentDefinition[]
  responsiveSectionOptions?: ResponsiveSectionOptions
}

type ResponsiveSectionOptions = {
  style?: ComponentDefinition['style']
  layoutResponsive?: ComponentDefinition['layoutResponsive']
}

type ComponentTypeOptions<T extends ComponentType> = T extends AppControllerType
  ? AppControllerOptions
  : T extends AppWidgetType
    ? AppWidgetOptions
    : T extends PageType
      ? PageOptions
      : {}

type ComponentTypeDefinition<T extends ComponentType> = T extends PageType
  ? PageDefinition
  : ComponentDefinition

export type CreateDefinition = <T extends ComponentType>(
  token: any,
  options: {
    componentType: T
  } & ComponentTypeOptions<T>,
) => Promise<ComponentTypeDefinition<T>>

export type UpdateSessionStateOptions = {stateMap: SessionStateMap}

export type {
  LanguageDefinition,
  LanguagesInitPayload,
  CompStructure,
  MenuItem,
  ColorPalette,
  FontsMap,
  TextThemeMap,
  RemoveOverridesOptions,
} from '@wix/document-services-types'

export type PageGroupDataPointer = Partial<Pointer>

export interface ComponentDefinition extends CompStructure {}

export interface PopupData extends PagesData {
  isPopup: true
  indexable: false
  mobileHidePage: true
}

export interface PopupPageDefinition extends PagesStructure {
  data: Partial<PopupData>
}

export type RouterPrefixValidationObject = {
  errorCode: string
  message: string
  valid: boolean
}

// NOTE: type is intentional, since it's already part of the public interface
export type ColorPalete = ColorPalette

export interface CustomMenuData extends MenuData {}
export interface BasicMenuItem extends MenuItem {}

export interface TransactionError extends Error {
  type: string
}

export type Layout = {
  fixedPosition?: boolean
  defaultPosition?: {region: string; placement: string}
  rotationInDegrees?: number
  scale?: number
  height: number
  width: number
  x: number
  y: number
}

export type Design = {
  color?: string
  colorOpacity?: number
  alignType?: string
  fittingType?: string
  scrollType?: string
  colorOverlay?: string
  colorOverlayOpacity?: number
  showOverlayForMediaType?: string
  imageOverlay?: string
  filterEffect?: string
  background: object
}

/**
 * The application.install options definitions.
 */
export type ApplicationInstallOptions = {
  /**
   * The application's appDefinitionId.
   */
  appDefinitionId: string
  /**
   * If set to *true*, the application is installed in silent mode, without prompts or navigations.
   */
  isSilent?: boolean
  /**
   * Additional information that an application passes to the installed application in the `origin.info` object.
   */
  originInfo?: Record<string, any>
  /**
   * The origin metaSite ID from which to clone the app data. Required when installing an application without adding components,
   * for example, when installing an app that can provision with different presets.
   */
  sourceTemplateId?: string
}

/**
 * The application.add options definitions.
 */
export type ApplicationUniversalInstallOptions = ApplicationInstallOptions & {
  /**
   * If set to *true*, shows the progress bar for the application installation process.
   * Works for Section Apps only.
   */
  showPageAddedPanel?: boolean
  /**
   * If set to *true*, doesn't open Add Panel for an application with category definition after the installation.
   * Works for Section Apps only.
   */
  disableAddPanel?: boolean
  /**
   * If set to *true*, navigates to the main application page after the installation.
   * Works for Section Apps only.
   */
  shouldNavigate?: boolean
  /**
   * The appDefinitionId of the app that can manage the pages added by this function.
   * For example, the ID of *Members*, which can manage other apps' pages. Works for Section Apps only.
   */
  managingAppDefId?: string
  /**
   * Installs only specified parts of an application. If not provided, installs a whole application. At this moment, only 'PLATFORM' part is supported.
   */
  componentTypes?: Extract<AppComponent, 'PLATFORM'>[]
}

/**
 * The application.isApplicationInstalled options definitions.
 */
export type ApplicationInstalledOptions = {
  /**
   * The application's appDefinitionId.
   */
  appDefinitionId: string
}

type AppComponent =
  | 'PLATFORM'
  | 'STUDIO'
  | 'STUDIO_WIDGET'
  | 'PAGE_OUT_OF_IFRAME'
  | 'WIDGET_OUT_OF_IFRAME'
  | 'WIDGET'
  | 'PAGE'

export type SignUpPrivacyNoteType = 'CHECKBOX' | 'NOTE'
